/**
 * The blank event-recap questionnaire a facilitator fills out after an event.
 * Ported verbatim from legacy/bcecloud/src/data/event-recap.js so a recap
 * submitted here is shaped exactly like the ones already in the database.
 *
 * `yes_no_na` questions may carry conditional follow-ups keyed by the chosen
 * answer (or `any`); a follow-up with `required: false` is optional.
 */

export interface RecapConditional {
  readonly question: string;
  readonly type: 'comment';
  readonly required?: boolean;
}

export interface RecapQuestion {
  readonly name: string;
  readonly question: string;
  readonly type: 'yes_no_na' | 'comment';
  /** Follow-ups keyed by 'Yes' | 'No' | 'N/A' | 'any'. */
  readonly conditionals?: Readonly<Record<string, RecapConditional>>;
}

export const RECAP_QUESTIONS: readonly RecapQuestion[] = [
  {
    name: 'trust_pilot_review',
    question: 'Should we send a Trust Pilot Review?',
    type: 'yes_no_na',
    conditionals: {
      No: { question: 'Why not?', type: 'comment' },
      Yes: { question: 'Is there anything we should know?', type: 'comment', required: false },
    },
  },
  {
    name: 'start_time',
    question: 'Did the program start on time?',
    type: 'yes_no_na',
    conditionals: {
      No: {
        question:
          'Why not? Was the program lengthened or shortened and what modifications did you make to the program because of the time change?',
        type: 'comment',
      },
    },
  },
  {
    name: 'loading_at_venue',
    question: 'Did loading/unloading at the venue go as planned?',
    type: 'yes_no_na',
    conditionals: { No: { question: 'Please give more details.', type: 'comment' } },
  },
  {
    name: 'assistants_on_time',
    question: 'Did the assistants arrive on time?',
    type: 'yes_no_na',
    conditionals: { No: { question: 'Please give more details.', type: 'comment' } },
  },
  {
    name: 'clients_response',
    question: 'Was the program well received by the client?',
    type: 'yes_no_na',
    conditionals: {
      any: {
        question: 'Please share any details both positive and negative that we should be aware of.',
        type: 'comment',
      },
    },
  },
  {
    name: 'warmup_activities',
    question: 'Please list ice-breakers and all activities conducted with the group.',
    type: 'comment',
  },
  {
    name: 'overall_suggessions',
    question:
      'What suggestions and comments would you like to share with Best Corporate Events pertaining to production logistics?',
    type: 'comment',
  },
  {
    name: 'delivery_suggestions',
    question:
      'What tips or suggestions can you share about the delivery of the program that other facilitators could benefit from?',
    type: 'comment',
  },
];

/** The suffix marking a stored conditional follow-up answer. */
export const RECAP_DETAIL_SUFFIX = '__detail';

/** The conditional follow-up that applies to a chosen answer, if any. */
export function conditionalFor(
  question: RecapQuestion,
  answer: string | undefined,
): RecapConditional | undefined {
  if (!answer || !question.conditionals) return undefined;
  return question.conditionals[answer] ?? question.conditionals['any'];
}
